<template>
<div class="cc-root" :style="themePageStyle">
  <nav class="cc-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.go(-1)" class="cc-back"><i class="mdi mdi-arrow-left"></i></a>
      <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" />
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Cart Checkout</span>
    </div>
  </div></nav>

  <div class="container cc-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <!-- ===== POST-CHECKOUT (orders created, payment in progress / retry / done) ===== -->
    <div v-else-if="success" class="cc-post">
      <!-- Order summary always visible at top -->
      <div class="cc-success-card text-center mb-3">
        <i class="mdi mdi-receipt-text-check" style="font-size:54px;color:#BFA164;"></i>
        <h4 class="mt-2" style="color:var(--fm-h,#F5F0E8);font-weight:700;">Orders created</h4>
        <p style="color:var(--fm-s,#6B6356);font-size:13px;margin-bottom:14px;">{{ success.orders.length }} order{{ success.orders.length===1?'':'s' }} from {{ success.sellerCount }} seller{{ success.sellerCount===1?'':'s' }} · Total <strong style="color:#BFA164;">KES {{ fNum(success.amount) }}</strong></p>
        <div style="font-size:11px;color:#8A827A;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Order group</div>
        <div style="color:#BFA164;font-family:monospace;font-size:13px;margin-bottom:14px;">{{ success.orderGroupId }}</div>
        <div v-for="o in success.orders" :key="o.id" class="cc-success-line">
          <span><span class="cc-mono">{{ o.order_number }}</span> · {{ o.title }}</span>
          <span>KES {{ fNum(o.amount) }}</span>
        </div>
      </div>

      <!-- ===== Payment status panel ===== -->
      <div class="cc-card cc-pay-panel">
        <!-- Waiting -->
        <div v-if="payStatus === 'pending' || payStatus === 'stk_sent'" class="text-center">
          <div class="cc-pulse-row">
            <div class="cc-pulse"></div>
            <span style="color:#50C878;font-weight:600;">Waiting for M-Pesa PIN</span>
          </div>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0 0 10px;">Check your phone <strong style="color:var(--fm-h,#F5F0E8);">{{ payPhone }}</strong> and enter your M-Pesa PIN.</p>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">
            <i class="mdi mdi-refresh mdi-spin me-1"></i>Checking payment status... ({{ pollCountdown }}s)
          </div>
        </div>

        <!-- Completed -->
        <div v-else-if="payStatus === 'success' || payStatus === 'completed'" class="text-center">
          <i class="mdi mdi-check-circle" style="font-size:54px;color:#50C878;"></i>
          <h5 style="color:#50C878;font-weight:700;margin-top:10px;">Payment confirmed!</h5>
          <p v-if="mpesaReceipt" style="color:var(--fm-m,#A09888);font-size:13px;margin:0;">M-Pesa Receipt: <strong style="color:var(--fm-h,#F5F0E8);">{{ mpesaReceipt }}</strong></p>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin-top:6px;">Each order is now in escrow. Sellers have been notified. A Flockr coordinator will contact you within 24h about transport.</p>
        </div>

        <!-- Failed / Cancelled / Timeout — show retry -->
        <div v-else-if="payStatus === 'failed' || payStatus === 'cancelled' || payStatus === 'timeout'" class="text-center">
          <i class="mdi mdi-close-circle" style="font-size:48px;color:#FF6B6B;"></i>
          <h5 style="color:#FF6B6B;font-weight:700;margin-top:8px;">{{ payStatus === 'cancelled' ? 'Payment cancelled' : (payStatus === 'timeout' ? 'No PIN entered in time' : 'Payment failed') }}</h5>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0 0 14px;">{{ payFailReason || 'You can resend the prompt — to the same phone or a different one.' }}</p>

          <div class="cc-retry-box">
            <h6 style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;margin-bottom:10px;">Try again</h6>
            <div class="d-flex gap-2 align-items-center justify-content-center mb-2">
              <span style="color:var(--fm-s,#6B6356);font-size:14px;font-weight:500;">+254</span>
              <input v-model="retryPhone" type="tel" class="cc-input" placeholder="7XX XXX XXX" style="max-width:240px;" maxlength="10" />
            </div>
            <button class="btn btn-warning fw-bold" @click="resendStk" :disabled="retryingStk || !retryPhoneValid">
              <span v-if="retryingStk"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
              <span v-else><i class="mdi mdi-cellphone-wireless me-1"></i>Resend M-Pesa Prompt</span>
            </button>
          </div>
        </div>

        <!-- Paybill fallback (always visible until completed) -->
        <div v-if="payStatus !== 'success' && payStatus !== 'completed'" class="cc-paybill mt-4">
          <h6 style="color:var(--fm-m,#A09888);font-size:13px;font-weight:600;margin-bottom:10px;"><i class="mdi mdi-bank me-1"></i>Or pay manually via M-Pesa Paybill</h6>
          <div class="cc-paybill-grid">
            <div><span class="cc-pb-lbl">Paybill</span><span class="cc-pb-val" style="color:#BFA164;">4160303</span></div>
            <div><span class="cc-pb-lbl">Account</span><span class="cc-pb-val">{{ accountRef }}</span></div>
            <div><span class="cc-pb-lbl">Amount</span><span class="cc-pb-val" style="color:#50C878;">KES {{ fNum(success.amount) }}</span></div>
          </div>
          <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:8px;"><i class="mdi mdi-information-outline me-1"></i>M-Pesa &gt; Lipa na M-Pesa &gt; Pay Bill &gt; enter the details above</p>
        </div>
      </div>

      <!-- Footer actions -->
      <div class="d-flex gap-2 justify-content-center mt-4 flex-wrap">
        <button class="btn btn-outline-warning" @click="$router.push('/orders')"><i class="mdi mdi-receipt me-1"></i>View My Orders</button>
        <button class="btn btn-outline-secondary" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront me-1"></i>Back to Marketplace</button>
      </div>
    </div>

    <!-- ===== EMPTY ===== -->
    <div v-else-if="!groups.length" class="text-center py-5">
      <i class="mdi mdi-cart-off" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Your cart is empty</h5>
      <button class="btn btn-warning mt-2" @click="$router.push('/marketplace')"><i class="mdi mdi-magnify me-1"></i>Find Animals</button>
    </div>

    <!-- ===== CHECKOUT FORM ===== -->
    <div v-else>
      <div v-if="error" class="cc-alert">{{ error }}</div>

      <div class="row g-4">
        <!-- LEFT: Order Summary -->
        <div class="col-lg-7">
          <h5 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin-bottom:16px;">{{ itemCount }} item{{ itemCount===1?'':'s' }} from {{ groups.length }} seller{{ groups.length===1?'':'s' }}</h5>

          <div v-for="g in groups" :key="g.seller_id" class="cc-group">
            <div class="cc-group-hd">
              <div class="d-flex align-items-center gap-2" style="min-width:0;">
                <div class="cc-avatar"><img v-if="g.seller_avatar" :src="g.seller_avatar" /><span v-else>{{ (g.seller_name||'S').charAt(0) }}</span></div>
                <div style="min-width:0;">
                  <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ g.seller_name }}</div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ g.items.length }} item{{ g.items.length===1?'':'s' }} · subtotal KES {{ fNum(g.subtotal) }}</div>
                </div>
              </div>
            </div>
            <div v-for="it in g.items" :key="it.cart_item_id" class="cc-item">
              <div style="flex:1;min-width:0;">
                <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ it.title }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ it.quantity }} × KES {{ fNum(it.unit_price) }}</div>
              </div>
              <div style="color:#BFA164;font-weight:600;font-size:13px;">KES {{ fNum(Number(it.unit_price) * Number(it.quantity)) }}</div>
            </div>
          </div>

          <!-- Transport notice -->
          <div class="cc-info">
            <i class="mdi mdi-truck-fast" style="font-size:24px;color:#BFA164;flex-shrink:0;"></i>
            <div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">Flockr will arrange transport for you</div>
              <div style="color:var(--fm-m,#A09888);font-size:12px;line-height:1.5;margin-top:4px;">
                Cart checkouts use Flockr-coordinated transport. We contact you within 24h to organise pickup from each seller and delivery to your location. <strong style="color:#FF9800;">Transport is invoiced separately</strong> after we agree pickup details.
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Delivery, Phone, Pay -->
        <div class="col-lg-5">
          <div class="cc-card" style="position:sticky;top:80px;">
            <h6 style="color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px;">Delivery</h6>
            <div style="position:relative;" class="mb-3">
              <label class="cc-label">Delivery address *</label>
              <div style="display:flex;gap:8px;">
                <div style="flex:1;position:relative;">
                  <input v-model="form.deliveryLocation" class="cc-input" placeholder="Search town, market, area..." @input="onLocSearch" @focus="showLocResults=true" autocomplete="off" style="padding-right:36px;" />
                  <i v-if="form.deliveryLatitude" class="mdi mdi-check-circle" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
                </div>
                <button type="button" class="cc-gps-btn" @click="useMyLocation" :disabled="geoLoading" title="Use my current location">
                  <i v-if="geoLoading" class="mdi mdi-loading mdi-spin"></i>
                  <i v-else class="mdi mdi-crosshairs-gps"></i>
                </button>
              </div>
              <div v-if="showLocResults && locResults.length" class="cc-loc-dropdown">
                <div v-for="(loc,li) in locResults" :key="li" class="cc-loc-item" @click="pickLocation(loc)">
                  <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;"></i>
                  <div><div style="color:var(--fm-h,#F5F0E8);font-size:12px;">{{ loc.display_name }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ loc.area }}</div></div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="cc-label">Notes for sellers (optional)</label>
              <textarea v-model="form.buyerNotes" class="cc-input" rows="2" placeholder="Anything sellers should know about your order..."></textarea>
            </div>

            <h6 style="color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin:18px 0 14px;">Payment</h6>
            <div class="mb-3">
              <label class="cc-label">M-Pesa phone *</label>
              <input v-model="form.phone" class="cc-input" placeholder="07XX XXX XXX or 2547XX XXX XXX" />
              <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">You'll get one M-Pesa STK push on this number for the full amount.</div>
            </div>

            <!-- Totals -->
            <div class="cc-totals">
              <div class="cc-total-row"><span>Items subtotal</span><span>KES {{ fNum(itemsSubtotal) }}</span></div>
              <div class="cc-total-row"><span>Platform fee (3%)</span><span>KES {{ fNum(platformFee) }}</span></div>
              <div v-if="insuranceTotal > 0" class="cc-total-row"><span><i class="mdi mdi-shield-check me-1" style="color:#50C878;"></i>Livestock insurance ({{ insureListingIds.length }} animal{{ insureListingIds.length===1?'':'s' }})</span><span style="color:#50C878;">KES {{ fNum(insuranceTotal) }}</span></div>
              <div v-if="productDeliveryTotal > 0" class="cc-total-row"><span><i class="mdi mdi-truck-delivery me-1" style="color:#4FC3F7;"></i>Product delivery (sellers deliver)</span><span style="color:#4FC3F7;">KES {{ fNum(productDeliveryTotal) }}</span></div>
              <div v-if="hasUnavailableProductDelivery" class="cc-total-row" style="font-size:11px;color:#FF9800;"><span><i class="mdi mdi-alert me-1"></i>Some sellers can't deliver to this address</span><span>Pickup may be available</span></div>
              <div v-if="groups.some(g=>g.hasListings)" class="cc-total-row"><span>Animal transport</span><span style="color:#FF9800;">Invoiced separately</span></div>
              <div class="cc-total-row cc-grand"><span>You pay now</span><span>KES {{ fNum(grandTotal) }}</span></div>
            </div>

            <button class="btn btn-warning w-100 mt-3" @click="submit" :disabled="submitting || !canSubmit">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-cellphone-wireless me-1"></i>
              {{ submitting ? 'Initiating M-Pesa...' : 'Pay with M-Pesa — KES ' + fNum(grandTotal) }}
            </button>
            <div style="font-size:11px;color:var(--fm-s,#6B6356);text-align:center;margin-top:8px;">
              Secured by escrow. Each seller is paid only after you confirm delivery.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .cc-root{background:#F7F5F2!important}
html[data-theme="light"] .cc-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .cc-card,.cc-group,.cc-success-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.cc-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.cc-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cc-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:4px;text-decoration:none}
.cc-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.cc-card,.cc-group,.cc-success-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px}
.cc-group{margin-bottom:14px;padding:0;overflow:hidden}
.cc-group-hd{padding:14px 18px;background:var(--fm-bg2,#0F0F0F);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cc-item{display:flex;justify-content:space-between;align-items:center;padding:12px 18px;border-bottom:1px solid var(--fm-br,#1A1A1A);gap:12px}
.cc-item:last-child{border-bottom:none}
.cc-avatar{width:36px;height:36px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-weight:700;color:#BFA164;flex-shrink:0;overflow:hidden}
.cc-avatar img{width:100%;height:100%;object-fit:cover}
.cc-info{display:flex;gap:12px;align-items:flex-start;background:rgba(191,161,100,.05);border:1px solid rgba(191,161,100,.2);border-left:3px solid #BFA164;border-radius:8px;padding:14px 16px;margin-top:14px}
.cc-label{display:block;color:var(--fm-m,#A09888);font-size:11px;font-weight:600;margin-bottom:4px}
.cc-input{width:100%;padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:var(--fm-h,#F5F0E8);font-size:13px;outline:none}
.cc-input:focus{border-color:#BFA164}
.cc-gps-btn{width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:#BFA164;cursor:pointer;flex-shrink:0}
.cc-loc-dropdown{position:absolute;left:0;right:50px;top:calc(100% + 4px);background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;max-height:240px;overflow-y:auto;z-index:10;box-shadow:0 8px 20px rgba(0,0,0,.4)}
.cc-loc-item{display:flex;gap:8px;padding:10px 12px;cursor:pointer;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.cc-loc-item:hover{background:rgba(191,161,100,.08)}
.cc-totals{margin-top:18px;padding-top:14px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-total-row{display:flex;justify-content:space-between;font-size:13px;color:var(--fm-m,#A09888);margin-bottom:6px}
.cc-total-row.cc-grand{font-size:16px;font-weight:700;color:var(--fm-h,#F5F0E8);padding-top:10px;margin-top:10px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-total-row.cc-grand span:last-child{color:#BFA164}
.cc-alert{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.3);color:#FF6B6B;padding:10px 14px;border-radius:6px;font-size:13px;margin-bottom:14px}
.cc-success-card{max-width:520px;margin:0 auto 24px;text-align:left}
.cc-success-line{display:flex;justify-content:space-between;font-size:12px;padding:6px 0;border-bottom:1px solid var(--fm-br,#1A1A1A);color:var(--fm-m,#A09888)}
.cc-success-line:last-child{border-bottom:none}
.cc-mono{font-family:monospace;color:#BFA164;font-size:11px}

/* Post-checkout payment panel */
.cc-post{max-width:620px;margin:0 auto}
.cc-pay-panel{padding:22px}
.cc-pulse-row{display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:8px}
.cc-pulse{width:10px;height:10px;border-radius:50%;background:#50C878;box-shadow:0 0 0 0 rgba(80,200,120,.6);animation:cc-pulse 1.6s infinite}
@keyframes cc-pulse{0%{box-shadow:0 0 0 0 rgba(80,200,120,.6)}70%{box-shadow:0 0 0 12px rgba(80,200,120,0)}100%{box-shadow:0 0 0 0 rgba(80,200,120,0)}}
.cc-retry-box{background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.2);border-radius:8px;padding:14px;max-width:380px;margin:0 auto}
.cc-paybill{padding-top:16px;border-top:1px solid var(--fm-br,#1E1E1E)}
.cc-paybill-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.cc-paybill-grid > div{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;padding:10px;display:flex;flex-direction:column;gap:2px}
.cc-pb-lbl{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px}
.cc-pb-val{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker } from "@/api";
export default {
  name: "CartCheckout",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,submitting:false,success:null,error:'',
    items:[],groups:[],itemCount:0,itemsSubtotal:0,
    // Insurance choices stashed by Cart.vue before navigation
    insureListingIds: [], insuranceQuotes: {},
    // Product delivery quotes — keyed by seller_id, computed when buyer fills delivery location
    productDeliveryQuotes: {}, productQuotesLoading: false,
    form:{deliveryLocation:'',deliveryLatitude:null,deliveryLongitude:null,phone:'',buyerNotes:''},
    locResults:[],showLocResults:false,locSearchTimer:null,geoLoading:false,
    // Payment status tracking (polled while in success state)
    payStatus:'pending', payFailReason:'', mpesaReceipt:'',
    payRequestId:'', payCheckoutRequestId:'', payPaymentRef:'', payPhone:'',
    pollTimer:null, pollCountdown:5,
    retryPhone:'', retryingStk:false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    platformFee(){return Math.round(this.itemsSubtotal * 0.03 * 100) / 100;},
    insuranceTotal(){
      var sum = 0; var qs = this.insuranceQuotes; var ids = this.insureListingIds;
      for (var i = 0; i < ids.length; i++) {
        var lid = ids[i];
        if (qs[lid] && qs[lid].available) sum += Number(qs[lid].premium || 0);
      }
      return sum;
    },
    productDeliveryTotal(){
      var sum = 0; var qs = this.productDeliveryQuotes;
      for (var sid in qs) {
        if (qs[sid] && qs[sid].canDeliver) sum += Number(qs[sid].fee || 0);
      }
      return sum;
    },
    hasUnavailableProductDelivery(){
      var qs = this.productDeliveryQuotes;
      for (var sid in qs) { if (qs[sid] && qs[sid].canDeliver === false) return true; }
      return false;
    },
    grandTotal(){return this.itemsSubtotal + this.platformFee + this.insuranceTotal + this.productDeliveryTotal;},
    canSubmit(){return this.form.deliveryLocation && this.form.deliveryLatitude && this.form.phone && this.form.phone.replace(/\D/g,'').length >= 9;},
    retryPhoneValid(){return this.retryPhone && this.retryPhone.replace(/\D/g,'').length >= 9;},
    accountRef(){return this.success && this.success.orderGroupId ? ('CART-' + this.success.orderGroupId.substring(0,8)) : '';}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    // Hydrate insurance choices stashed by Cart.vue
    try {
      this.insureListingIds = JSON.parse(sessionStorage.getItem('flockr_cart_insure_ids') || '[]');
      this.insuranceQuotes  = JSON.parse(sessionStorage.getItem('flockr_cart_insure_quotes') || '{}');
    } catch (e) {
      this.insureListingIds = []; this.insuranceQuotes = {};
    }
    this.load();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();if(this.locSearchTimer)clearTimeout(this.locSearchTimer);this.stopPolling()},
  watch: {
    'form.deliveryLatitude'(v){ if (v) this.refreshProductQuotes(); }
  },
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    fNum(n){return Number(n||0).toLocaleString()},
    async refreshProductQuotes(){
      // Pull a delivery quote per seller-with-products from the server using buyer's lat/lng
      var sellersWithProducts = {};
      this.groups.forEach(function(g){
        if (g.hasProducts) sellersWithProducts[g.seller_id] = true;
      });
      var sids = Object.keys(sellersWithProducts);
      if (!sids.length || !this.form.deliveryLatitude) return;
      this.productQuotesLoading = true;
      var fresh = {};
      for (var i = 0; i < sids.length; i++) {
        try {
          var r = await executeJson({
            function: 'calculateProductDeliveryFee',
            sellerId: sids[i],
            lat: this.form.deliveryLatitude,
            lng: this.form.deliveryLongitude
          }, flocker);
          if (r.data && r.data.success) fresh[sids[i]] = r.data.data || {};
          else fresh[sids[i]] = { canDeliver:false, reason: (r.data && r.data.message) || 'Could not get quote' };
        } catch(e){ fresh[sids[i]] = { canDeliver:false, reason:'Network error' }; }
      }
      this.productDeliveryQuotes = fresh;
      this.productQuotesLoading = false;
    },
    async load(){
      this.loading=true;
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      try{
        var r=await executeJson({function:'getCart',userId:userId},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.items=r.data.data.items||[];
          // Both listings (animals) and products in cart — listings ship via Flockr-arranged
          // transport, products ship via the seller's own delivery (Jiji-style).
          this.groups=(r.data.data.groups||[]).map(function(g){
            g.subtotal = g.items.reduce(function(s,i){return s + Number(i.unit_price)*Number(i.quantity);},0);
            g.hasProducts = g.items.some(function(i){return i.item_type === 'product';});
            g.hasListings = g.items.some(function(i){return i.item_type === 'listing';});
            return g;
          });
          this.itemCount=this.groups.reduce(function(s,g){return s+g.items.length;},0);
          this.itemsSubtotal=this.groups.reduce(function(s,g){return s+g.subtotal;},0);
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    onLocSearch(){
      if(this.locSearchTimer)clearTimeout(this.locSearchTimer);
      var self=this;var q=String(this.form.deliveryLocation||'').trim();
      if(q.length<3){this.locResults=[];this.showLocResults=false;return}
      this.locSearchTimer=setTimeout(function(){self.doLocSearch(q)},400);
    },
    async doLocSearch(q){
      try{
        var url='https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=6&q='+encodeURIComponent(q);
        var res=await fetch(url);var data=await res.json();
        this.locResults=data.map(function(d){var parts=(d.display_name||'').split(',');return{display_name:parts.slice(0,3).join(', ').trim(),area:parts.slice(1,4).join(', ').trim(),lat:d.lat,lon:d.lon}});
        this.showLocResults=true;
      }catch(e){this.locResults=[]}
    },
    pickLocation(loc){
      this.form.deliveryLocation=loc.display_name;
      this.form.deliveryLatitude=Number(loc.lat);this.form.deliveryLongitude=Number(loc.lon);
      this.locResults=[];this.showLocResults=false;
    },
    useMyLocation(){
      if(!navigator.geolocation)return;
      this.geoLoading=true;var self=this;
      navigator.geolocation.getCurrentPosition(
        function(pos){
          self.form.deliveryLatitude=pos.coords.latitude;self.form.deliveryLongitude=pos.coords.longitude;
          fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat='+pos.coords.latitude+'&lon='+pos.coords.longitude)
            .then(function(r){return r.json()}).then(function(d){
              if(d&&d.display_name){var parts=d.display_name.split(',');self.form.deliveryLocation=parts.slice(0,3).join(',').trim()}
              self.geoLoading=false;
            }).catch(function(){self.geoLoading=false});
        },
        function(){self.geoLoading=false},
        {enableHighAccuracy:true,timeout:10000}
      );
    },
    async submit(){
      if(!this.canSubmit){this.error='Please complete delivery location and M-Pesa phone';return}
      this.error='';this.submitting=true;
      var userId=sessionStorage.getItem('userId');
      try{
        var r=await executeJson({
          function:'createCartCheckout',
          userId:userId,
          deliveryLocation:this.form.deliveryLocation,
          deliveryLatitude:this.form.deliveryLatitude||'',
          deliveryLongitude:this.form.deliveryLongitude||'',
          phone:this.form.phone,
          buyerNotes:this.form.buyerNotes||'',
          // Auto-policy: list of listing IDs the buyer ticked "Protect this animal" for.
          // Server re-computes the actual premium so we can't be tricked into paying less.
          insureListingIds: JSON.stringify(this.insureListingIds || [])
        },flocker);
        if(r.data && r.data.success && r.data.data){
          this.success=r.data.data;
          // Capture payment tracking handles
          this.payRequestId = r.data.data.requestId || '';
          this.payCheckoutRequestId = r.data.data.checkoutRequestId || '';
          this.payPaymentRef = r.data.data.paymentRef || '';
          this.payPhone = r.data.data.phone || this.form.phone;
          this.retryPhone = (r.data.data.phone || this.form.phone || '').replace(/^254/, '');
          this.payStatus = 'stk_sent';
          this.startPolling();
          // Clear local cart cache + insurance stash + bump topbar badge
          sessionStorage.setItem('flockr_cart_count',0);
          sessionStorage.removeItem('flockr_cart_insure_ids');
          sessionStorage.removeItem('flockr_cart_insure_quotes');
          window.dispatchEvent(new CustomEvent('flockr-cart-updated',{detail:{count:0}}));
        } else {
          this.error=(r.data && r.data.message) || 'Checkout failed';
        }
      }catch(e){
        this.error=(e.response&&e.response.data&&e.response.data.message)||'Network error — please try again';
      }
      this.submitting=false;
    },

    // ===== Payment polling =====
    startPolling(){
      this.stopPolling();
      this.pollCountdown = 5;
      var self = this;
      this.pollTimer = setInterval(function(){
        self.pollCountdown--;
        if (self.pollCountdown <= 0) { self.pollCountdown = 5; self.checkPayment(); }
      }, 1000);
    },
    stopPolling(){
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
    async checkPayment(){
      if (!this.payRequestId && !this.payCheckoutRequestId && !this.payPaymentRef) return;
      try {
        var f = new FormData(); f.append('function','checkPaymentStatus');
        if (this.payRequestId) f.append('requestId', this.payRequestId);
        if (this.payCheckoutRequestId) f.append('checkoutRequestId', this.payCheckoutRequestId);
        if (this.payPaymentRef) f.append('paymentRef', this.payPaymentRef);
        var r = await execute(f, flocker);
        if (r.data && r.data.data) {
          var st = r.data.data.status;
          var msg = r.data.data.message || '';
          if (st === 'success' || st === 'completed') {
            this.payStatus = 'success';
            this.mpesaReceipt = r.data.data.paymentRef || r.data.data.mpesaReceipt || '';
            this.stopPolling();
          } else if (st === 'cancelled') {
            this.payStatus = 'cancelled'; this.payFailReason = msg; this.stopPolling();
          } else if (st === 'failed') {
            this.payStatus = 'failed'; this.payFailReason = msg; this.stopPolling();
          } else if (st === 'timeout') {
            this.payStatus = 'timeout'; this.payFailReason = msg || 'No PIN entered in 5 minutes'; this.stopPolling();
          }
        }
      } catch(e) {}
    },
    async resendStk(){
      if (!this.retryPhoneValid) return;
      this.retryingStk = true;
      var userId = sessionStorage.getItem('userId');
      var phoneRaw = this.retryPhone.replace(/\D/g, '');
      // accept either "07.." or "7..": always normalize to 2547..
      var phone = phoneRaw.charAt(0) === '0' ? ('254' + phoneRaw.substring(1))
                : (phoneRaw.length === 9 ? ('254' + phoneRaw) : phoneRaw);
      try {
        var r = await executeJson({
          function: 'retryCartPayment',
          userId: userId,
          orderGroupId: this.success.orderGroupId,
          phone: phone
        }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.payRequestId = r.data.data.requestId || '';
          this.payCheckoutRequestId = r.data.data.checkoutRequestId || '';
          this.payPaymentRef = r.data.data.paymentRef || '';
          this.payPhone = r.data.data.phone || phone;
          this.payStatus = 'stk_sent';
          this.payFailReason = '';
          this.startPolling();
        } else {
          this.payFailReason = (r.data && r.data.message) || 'Could not resend the M-Pesa prompt';
          this.payStatus = 'failed';
        }
      } catch(e) {
        this.payFailReason = (e.response && e.response.data && e.response.data.message) || 'Network error — please try again';
        this.payStatus = 'failed';
      }
      this.retryingStk = false;
    }
  }
};
</script>
